import Vue from 'vue'
import { getAuth } from 'firebase/auth'
import { getFirestore, doc, onSnapshot, getDocFromServer } from 'firebase/firestore'
import { subscribeAuthBootstrap, shouldDebugFirestoreAuth } from '@/services/authBootstrap'

const MAX_RETRIES = 4
const RETRY_BASE_MS = 300
const VERDICT_TIMEOUT_MS = 20000

/**
 * `status` is the single source of truth for employee access:
 *   'unknown'    — nothing has resolved yet (app just booted)
 *   'signed-out' — no Firebase user
 *   'checking'   — attached for `uid`, waiting on a server answer
 *   'active'     — employees/{uid}.active === true
 *   'inactive'   — the server confirmed the account is not active
 *   'error'      — the record could not be read (retries exhausted / timed out)
 *
 * `uid` always names the user the status belongs to. Callers must compare it
 * against the signed-in user before acting: a verdict left over from a previous
 * session (or from being signed out) is otherwise indistinguishable from a real
 * "this account is inactive" answer, which is how a fully activated employee
 * ended up staring at the pending-activation screen.
 */
const employeeState = Vue.observable({
  status: 'unknown',
  uid: null,
  employee: null,
  error: null,
})

let started = false
let unsubscribeBootstrap = null
let unsubscribeListener = null
let verdictTimer = null
let generation = 0

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function dbg(tag, payload) {
  if (!shouldDebugFirestoreAuth()) return
  console.log(`[useEmployee] ${tag}`, payload)
}

function clearListener() {
  if (typeof unsubscribeListener === 'function') {
    try { unsubscribeListener() } catch (e) { /* already torn down */ }
  }
  unsubscribeListener = null
}

function clearVerdictTimer() {
  if (verdictTimer) clearTimeout(verdictTimer)
  verdictTimer = null
}

function settle(gen, status) {
  if (gen !== generation) return
  clearVerdictTimer()
  employeeState.error = null
  employeeState.status = status
  dbg('verdict', { uid: employeeState.uid, status })
}

/**
 * A listen that loses permission mid-flight can surface as a snapshot saying the
 * document vanished rather than as an error, which would read as "this account
 * is inactive". Confirm a missing document with a direct server read before
 * sending anyone to the pending-activation screen.
 */
function confirmMissingDoc(ref, gen) {
  getDocFromServer(ref)
    .then((snap) => {
      if (gen !== generation) return
      const data = snap.exists() ? (snap.data() || {}) : null
      dbg('confirmMissingDoc', {
        path: ref.path,
        exists: snap.exists(),
        active: data ? data.active : null,
        document: data,
      })
      if (data && data.active === true) {
        employeeState.employee = data
        settle(gen, 'active')
        return
      }

      // The document is absent in the Firebase project this browser reached.
      // That is a configuration/data mismatch, not proof that an employee has
      // been deliberately deactivated. Never render the activation screen for
      // this case: it is reserved exclusively for an existing server document
      // whose `active` field is explicitly false.
      clearVerdictTimer()
      employeeState.employee = null
      employeeState.error = new Error(`Employee record was not found at ${ref.path}.`)
      employeeState.status = 'error'
      dbg('verdict', { uid: employeeState.uid, status: 'error', reason: 'employee-record-not-found' })
    })
    .catch((err) => {
      if (gen !== generation) return
      dbg('confirmMissingDoc:error', { path: ref.path, code: err?.code, message: err?.message })
      clearVerdictTimer()
      employeeState.error = err
      employeeState.status = 'error'
    })
}

function attach(uid) {
  clearListener()
  clearVerdictTimer()

  const gen = ++generation
  employeeState.uid = uid
  employeeState.employee = null
  employeeState.error = null
  employeeState.status = 'checking'

  let retries = 0
  const db = getFirestore()
  const ref = doc(db, 'employees', uid)
  dbg('attach', {
    projectId: db.app?.options?.projectId,
    path: ref.path,
    uid,
  })

  // A listen that neither answers nor errors (offline, a stalled multi-tab
  // primary) would otherwise leave the console spinning forever, so fall back to
  // a retryable error rather than an indeterminate wait.
  verdictTimer = setTimeout(() => {
    if (gen !== generation || employeeState.status !== 'checking') return
    employeeState.error = new Error('Timed out reading the employee record.')
    employeeState.status = 'error'
  }, VERDICT_TIMEOUT_MS)

  const subscribe = () => {
    if (gen !== generation) return

    unsubscribeListener = onSnapshot(
      ref,
      (snapshot) => {
        if (gen !== generation) return
        retries = 0

        const fromCache = snapshot.metadata.fromCache
        const data = snapshot.exists() ? (snapshot.data() || {}) : null
        dbg('snapshot', {
          path: ref.path,
          exists: snapshot.exists(),
          fromCache,
          active: data ? data.active : null,
          document: data,
        })

        if (snapshot.exists()) {
          employeeState.employee = data
          if (data.active === true) {
            settle(gen, 'active')
            return
          }
          // A cached copy can predate an activation, so only a server read is
          // allowed to send someone to the pending-activation screen.
          if (!fromCache) settle(gen, 'inactive')
          return
        }

        if (fromCache) return
        employeeState.employee = null
        confirmMissingDoc(ref, gen)
      },
      (err) => {
        if (gen !== generation) return
        console.warn('[useEmployee] listener error:', err?.code || err, 'path:', ref.path)
        clearListener()

        const isRetryable = err?.code === 'permission-denied' || err?.code === 'unavailable'
        if (isRetryable && retries < MAX_RETRIES) {
          retries += 1
          employeeState.error = null
          employeeState.status = 'checking'

          const authUser = getAuth().currentUser
          const refresh = authUser && authUser.uid === uid
            ? authUser.getIdToken(true).catch((refreshErr) => {
              console.warn('[useEmployee] token refresh failed:', refreshErr?.code || refreshErr)
            })
            : Promise.resolve()

          refresh
            .then(() => sleep(RETRY_BASE_MS * retries))
            .then(subscribe)
          return
        }

        clearVerdictTimer()
        employeeState.error = err
        employeeState.status = 'error'
      }
    )
  }

  subscribe()
}

export function startEmployeeListener() {
  if (started) return
  started = true

  // Driving this off the auth bootstrap rather than onAuthStateChanged means the
  // listener only attaches once the ID token has been refreshed and fenced, so
  // the first read cannot race ahead of Firestore's credentials and come back
  // permission-denied.
  unsubscribeBootstrap = subscribeAuthBootstrap((payload) => {
    const authUser = payload?.authUser || null

    if (!authUser) {
      clearListener()
      clearVerdictTimer()
      generation += 1
      employeeState.uid = null
      employeeState.employee = null
      employeeState.error = null
      employeeState.status = 'signed-out'
      return
    }

    dbg('bootstrapPayload', { uid: authUser.uid, email: authUser.email })
    if (employeeState.uid === authUser.uid && unsubscribeListener) return
    attach(authUser.uid)
  })
}

export function stopEmployeeListener() {
  clearListener()
  clearVerdictTimer()
  generation += 1
  if (typeof unsubscribeBootstrap === 'function') {
    try { unsubscribeBootstrap() } catch (e) { /* already torn down */ }
  }
  unsubscribeBootstrap = null
  started = false
  employeeState.uid = null
  employeeState.employee = null
  employeeState.error = null
  employeeState.status = 'unknown'
}

export function retryEmployeeListener() {
  const authUser = getAuth().currentUser
  if (!authUser) return
  attach(authUser.uid)
}

export function useEmployee() {
  startEmployeeListener()

  return {
    state: employeeState,
    retry: retryEmployeeListener,
  }
}
